/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Input, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';

import template from './site-country-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';
import {ALL_COUNTRIES} from '../../common/directives/country-select/countries';
import {SiteApiService} from '../../site/site-api.service';

import {TARGETING_IMPORTS} from '../targeting-imports';
import {TargetingTemplateComponent} from './targeting-template.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateComponent],
  selector: 'adn-site-country-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteCountryTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.siteCountry;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  readonly siteCountries = signal<any[]>([]);
  readonly resolved = signal(false);

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      const model = this.model || {};
      const selected = model.countries || [];
      const summary = (model.negated === true ? 'NOT: ' : '') + _.map(
        _.filter(this.siteCountries(), (siteCountry) => selected.indexOf(siteCountry.code) > -1),
        'name'
      ).join(', ');
      return {
        vm: _.cloneDeep(model),
        model: _.cloneDeep(model),
        constant: ADN_TARGETING_TYPES.siteCountry,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly siteApi: SiteApiService
  ) {
  }

  ngOnInit(): void {
    this.ensureModel();

    if (!this.tabController) {
      return;
    }

    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.siteCountry.widget, () => this.loadSiteCountries(), copyTemplate);
  }

  selectAll(): void {
    this.model.countries = _.map(this.siteCountries(), 'code');
  }

  deselectAll(): void {
    this.model.countries = [];
  }

  isSelected(code: string): boolean {
    return (this.model.countries || []).indexOf(code) > -1;
  }

  toggleCountry(code: string): void {
    this.model.countries = this.model.countries || [];
    const idx = this.model.countries.indexOf(code);
    if (idx > -1) {
      this.model.countries.splice(idx, 1);
    } else {
      this.model.countries.push(code);
    }
  }

  private ensureModel(): void {
    if (!this.model) {
      return;
    }
    this.model.countries = this.model.countries || [];
    if (_.get(this.model.countries, '[0].code')) {
      this.model.countries = _.map(this.model.countries, (s) => s.code);
    }
    if (_.isNil(this.model.negated)) {
      this.model.negated = false;
    }
  }

  private loadSiteCountries(): void {
    if (this.resolved()) {
      return;
    }
    this.siteApi.getAvailableCountries().subscribe((page) => {
      const siteCountries: any[] = [];
      _.forEach(page.countries, (country: any) => {
        if (!country.country) {
          return;
        }
        const countryObj: any = _.find(ALL_COUNTRIES, (c) => c.code === country.country) || {
          code: country.country,
          name: country.country
        };
        countryObj.count = country.count;
        siteCountries.push(countryObj);
      });
      this.siteCountries.set(siteCountries);
      this.resolved.set(true);
    });
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || {countries: []};
    Object.keys(this.model).forEach((key) => delete this.model[key]);
    Object.assign(this.model, copied);
    this.ensureModel();
  }
}
